import React, { useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import Box from "@mui/material/Box";
import { Trans, useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import LoginExistingUser from "./LoginExistingUser";
import { LoginOrRegister } from "./LoginOrRegister";
import EnterCode from "./EnterCode";
import BoxContainer from "./BoxContainer";
import { useAuth } from "../../providers/AuthProvider";
import BoxHeading from "./BoxHeading";
import NotReceivedCodeDialog from "./NotReceivedCodeDialog";
import UserLoggedInBox from "./UserLoggedInBox";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import LoginWarningDialog from "../LoginWarningDialog";
import MessageDialog from "../../../components/Common/MessageDialog";
import "../../types/Theme";
const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: "#202529",
        display: "flex",
        flexGrow: 1,
        justifyContent: "center",
        width: "100%",
        overflow: "hidden",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
        },
    },
    pageContainer: {
        margin: "0 auto 0 auto",
        maxWidth: "1152px",
        flexGrow: 1,
        display: "flex",
        justifyContent: "flex-end",
        position: "relative",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            position: "fixed",
            top: 0,
            left: 0,
            height: "100vh",
            width: "100%",
            justifyContent: "flex-start",
            maxHeight: "-webkit-fill-available",
        },
    },
    backgroundContainer: Object.assign({ display: "flex", margin: "0 auto 0 auto", flexGrow: 1, position: "relative", flexShrink: 0, [theme.breakpoints.only("xs")]: {
            width: "100%",
        } }, (theme.clientType == "mobile" && {
        [theme.breakpoints.between("sm", "md")]: {
            width: "100%",
            height: "100%",
        },
    })),
    backgroundInnerContainer: {
        position: "absolute",
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "25% 100%",
        backgroundSize: "auto 100%",
        [`${theme.breakpoints.down("sm")}, ${theme.breakpoints.down("md")} and (orientation:portrait)`]: {
            height: "50vh",
            backgroundPosition: "14% 0%",
            backgroundSize: "auto 100%",
            //backgroundColor: 'red',
            //boxShadow: '0 0 8px 8px #202529 inset',
        },
        [theme.breakpoints.only("sm")]: {
        //backgroundPosition: '10% 100%',
        },
    },
    backgroundBottomShadow: {
        position: "absolute",
        left: 0,
        bottom: 0,
        width: "100%",
        height: "70px",
        background: `linear-gradient(180deg, ${alpha(window.PAGE_SETTINGS.background.color, 0)} 0%, ${window.PAGE_SETTINGS.background.color} 100%)`,
    },
    formContainer: {
        flexBasis: "480px",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down("sm")]: {
            flexBasis: "100px",
            marginRight: theme.spacing(3),
            marginLeft: theme.spacing(3),
            paddingBottom: 0,
            marginTop: 0,
            marginBottom: theme.spacing(3),
        },
    },
    button: {
        minWidth: "136px",
        paddingRight: 0,
        paddingLeft: 0,
        [theme.breakpoints.down("sm")]: {
            minWidth: "96px",
        },
    },
    spacer: {},
    columnSpacer: {
        flexGrow: 1,
        [theme.breakpoints.down("sm")]: {
            flexGrow: 0,
            flexBasis: "50vh",
            flexShrink: 1,
        },
    },
    bottomContainer: {
        marginTop: theme.spacing(4),
    },
    alreadyHaveAccountButton: {
        fontSize: "20px",
        marginTop: 0,
        marginBottom: 0,
    },
}));
const FrontPage = ({ handleLoginSuccess, handleLoginSuccessNewCode, }) => {
    var _a, _b, _c, _d;
    const classes = useStyles();
    const { t } = useTranslation();
    const [state, setState] = useState("");
    const { isAuthenticated } = useAuth();
    const [pendingLoginResult, setPendingLoginResult] = useState(null);
    const [showCodeInFieldWarning, setShowCodeInFieldWarning] = useState(false);
    const [code, setCode] = useState("");
    const [shouldShowLoggedOutMessage, setShouldShowLoggedOutMessage] = useState(() => {
        const flag = sessionStorage.getItem("justLoggedOut") === "true";
        if (flag)
            sessionStorage.removeItem("justLoggedOut");
        return flag;
    });
    const [codeData, setCodeData] = useState(() => {
        // If we pass code data from the script:
        const phpData = window.PROPHOTO_CODE_DATA;
        if (!phpData)
            return null;
        return {
            name: phpData.name,
            error: phpData.error,
            accessCode: phpData.accessCode,
            errorMessage: phpData.errorMessage,
        };
    });
    const [displayNotReceivedCodeDialog, setDisplayNotReceivedCodeDialog] = useState(false);
    const theme = useTheme();
    const setStateByHash = (hash) => {
        if (hash === "#login") {
            setState("login");
            // Clear hash so we don't go to login state again
            history.replaceState(null, "", "#");
        }
    };
    useEffect(() => {
        if (codeData && !codeData.error) {
            setState("login-or-register");
        }
        else {
            // Only leave the code screen. A bare setState("") here also runs on
            // mount, where it cannot help - the initial state is already "" - but
            // can overwrite a state set in between, so a "Sign in" click landing
            // before this effect flushes would silently do nothing.
            setState((current) => (current === "login-or-register" ? "" : current));
        }
    }, [codeData]);
    useEffect(() => {
        if (window.location.hash) {
            setStateByHash(window.location.hash);
        }
    }, []);
    const hashChangeHandler = useCallback(() => {
        setStateByHash(window.location.hash);
    }, []);
    // Hash change listener only works on desktop. May be related
    // to react-router on mobile?
    useEffect(() => {
        window.addEventListener("hashchange", hashChangeHandler);
        return () => {
            window.removeEventListener("hashchange", hashChangeHandler);
        };
    }, [hashChangeHandler]);
    const handleSetStateLogin = () => {
        if (code.trim()) {
            setShowCodeInFieldWarning(true);
            return;
        }
        setState("login");
    };
    const loginSuccessHandler = useCallback((loginResult) => {
        if (loginResult === null || loginResult === void 0 ? void 0 : loginResult.warning) {
            setPendingLoginResult({
                finalHandler: handleLoginSuccess,
                loginResult,
            });
            return;
        }
        handleLoginSuccess(loginResult);
    }, [handleLoginSuccess]);
    const loginSuccessNewCodeHandler = useCallback((loginResult) => {
        if (loginResult === null || loginResult === void 0 ? void 0 : loginResult.warning) {
            setPendingLoginResult({
                finalHandler: handleLoginSuccessNewCode,
                loginResult,
            });
            return;
        }
        handleLoginSuccessNewCode(loginResult);
    }, [handleLoginSuccessNewCode]);
    const handleLoginWarningConfirm = () => {
        if (!pendingLoginResult) {
            return;
        }
        const { finalHandler, loginResult } = pendingLoginResult;
        setPendingLoginResult(null);
        finalHandler(loginResult);
    };
    const { background } = window.PAGE_SETTINGS;
    return (React.createElement("div", { className: classes.root, style: { backgroundColor: background.color } },
        React.createElement("div", { className: classes.backgroundContainer },
            React.createElement("div", { className: classes.backgroundInnerContainer, style: { backgroundImage: `url(${background.image}` } }, theme.clientType != "desktop" && (React.createElement("div", { className: classes.backgroundBottomShadow }))),
            React.createElement("div", { className: classes.pageContainer },
                React.createElement("div", { className: classes.columnSpacer }),
                React.createElement("div", { className: classes.formContainer },
                    React.createElement("div", { className: classes.spacer }),
                    state == "login" && (React.createElement(LoginExistingUser, { handleLoginSuccess: loginSuccessHandler, handleGoBack: () => setState("") })),
                    state == "login-or-register" && (React.createElement(LoginOrRegister, { codeInfo: {
                            name: (_a = codeData === null || codeData === void 0 ? void 0 : codeData.name) !== null && _a !== void 0 ? _a : "",
                            code: (_b = codeData === null || codeData === void 0 ? void 0 : codeData.accessCode) !== null && _b !== void 0 ? _b : "",
                        }, handleLoginSuccess: loginSuccessNewCodeHandler, handleGoBack: () => setState("") })),
                    state == "" && (React.createElement(React.Fragment, null,
                        React.createElement(EnterCode, { handleSetCodeData: (data) => setCodeData(Object.assign({ error: false }, data)), setDisplayNotReceivedCodeDialog: setDisplayNotReceivedCodeDialog, 
                            // If we're authenticated, checking a code will log you in:
                            handleLoginSuccess: handleLoginSuccessNewCode, codeData: codeData, code: code, setCode: setCode, autoSubmitCode: (_c = window.PROPHOTO_CODE_DATA) === null || _c === void 0 ? void 0 : _c.accessCode }),
                        isAuthenticated && React.createElement(UserLoggedInBox, null),
                        !isAuthenticated && (React.createElement(React.Fragment, null,
                            React.createElement(BoxContainer, { className: classes.bottomContainer },
                                React.createElement(Box, { display: "flex", justifyContent: "space-between", alignItems: "center" },
                                    React.createElement(BoxHeading, { className: classes.alreadyHaveAccountButton },
                                        React.createElement(Trans, null, "Already have an account?")),
                                    React.createElement(Button, { variant: "contained", color: "primary", size: "large", className: classes.button, onClick: handleSetStateLogin, "data-testid": "login-button" },
                                        React.createElement(Trans, null, "Sign in"))))))))))),
        displayNotReceivedCodeDialog && (React.createElement(NotReceivedCodeDialog, { onClose: () => setDisplayNotReceivedCodeDialog(false) })),
        ((_d = pendingLoginResult === null || pendingLoginResult === void 0 ? void 0 : pendingLoginResult.loginResult) === null || _d === void 0 ? void 0 : _d.warning) && (React.createElement(LoginWarningDialog, { warning: pendingLoginResult.loginResult.warning, onConfirm: handleLoginWarningConfirm })),
        showCodeInFieldWarning && (React.createElement(MessageDialog, { heading: t("Did you want to add this code?"), content: React.createElement(React.Fragment, null,
                React.createElement("p", null, t("You have entered '%s' in the Code field.", {
                    postProcess: "sprintf",
                    sprintf: [code],
                })),
                React.createElement("p", null, t("To add this code to your account please close this dialogue and press the ‘Continue’ button"))), onOk: () => setShowCodeInFieldWarning(false), "data-testid": "code-input-warning" })),
        shouldShowLoggedOutMessage && (React.createElement(MessageDialog, { heading: t("You have been successfully logged out"), content: React.createElement(Box, { textAlign: "center" }, t("Thank you for using our services!")), onOk: () => setShouldShowLoggedOutMessage(false), onBackdropPress: () => setShouldShowLoggedOutMessage(false), "data-testid": "logged-out-success-dialog" }))));
};
export default FrontPage;
